import { ability, ACTIONS, PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import { getTeamIdForTemplateVisibility } from '@heidi/templates/data';
import {
  useCombinedQueriesTeamGeneralInfo,
  useOrganizationInfo,
} from '@heidi/workspace/data';
import { useCallback, useMemo } from 'react';

export { getTeamIdForTemplateVisibility } from '@heidi/templates/data';

type TemplateSharingTarget = {
  team_id?: string | null;
  template_visibility?: TemplateVisibility | null;
  is_editable?: boolean;
};

export const useTemplateSharing = () => {
  const { isOrganizationTemplateManager } = useOrganizationInfo();
  const { isTeamAdmin, joinedTeamsData, teamOrgId } = useCombinedQueriesTeamGeneralInfo();

  const userTeamIds = useMemo(() => {
    return new Set(joinedTeamsData?.joined_teams?.map((team) => team.team_id) ?? []);
  }, [joinedTeamsData]);

  const hasTemplateSharingManagePermission = ability.can(
    ACTIONS.ACCESS,
    PERMISSION_SUBJECTS.TEMPLATE_SHARING_MANAGE
  );

  // Org-wide and Specific-Teams sharing controls are reserved for org-level
  // Template Managers (and global admins). Sub-team Template Managers fall
  // through to the simple Team flow — they keep team-admin-equivalent powers
  // within their team, but cannot org-publish or share into sibling teams.
  const canShareBroadly = isOrganizationTemplateManager;

  const isTemplateFromUserTeam = useCallback(
    (template?: TemplateSharingTarget | null) => {
      if (!template?.team_id) return false;
      return userTeamIds.has(template.team_id);
    },
    [userTeamIds]
  );

  const canManageOwnTeamTemplate = useCallback(
    (template?: TemplateSharingTarget | null) => {
      return Boolean(
        isTemplateFromUserTeam(template) && hasTemplateSharingManagePermission
      );
    },
    [hasTemplateSharingManagePermission, isTemplateFromUserTeam]
  );

  const canEditTemplateVisibility = useCallback(
    (template: TemplateSharingTarget) => {
      if (isOrganizationTemplateManager) return true;

      const isOwnTeamTemplate = isTemplateFromUserTeam(template);
      if (template.team_id && !isOwnTeamTemplate && template.is_editable === false) {
        return false;
      }

      // Promoting/demoting Organization visibility is reserved for org-level
      // managers. Team-admin-equivalent roles (Team Admin, sub-team Template
      // Manager) cannot change a template's Organization visibility.
      if (template.template_visibility === TemplateVisibility.Organization) {
        return false;
      }

      if (template.template_visibility === TemplateVisibility.Team) {
        return Boolean(
          isOwnTeamTemplate && (isTeamAdmin || hasTemplateSharingManagePermission)
        );
      }

      return true;
    },
    [
      hasTemplateSharingManagePermission,
      isOrganizationTemplateManager,
      isTeamAdmin,
      isTemplateFromUserTeam,
    ]
  );

  const getTeamIdForVisibility = useCallback(
    ({
      visibility,
      templateTeamId,
      fallbackTeamId = teamOrgId,
      includeOrganization,
    }: {
      visibility?: TemplateVisibility | null;
      templateTeamId?: string | null;
      fallbackTeamId?: string | null;
      includeOrganization?: boolean;
    }) => {
      return getTeamIdForTemplateVisibility({
        visibility,
        templateTeamId,
        fallbackTeamId,
        includeOrganization,
      });
    },
    [teamOrgId]
  );

  return {
    canEditTemplateVisibility,
    canManageOwnTeamTemplate,
    canShareBroadly,
    getTeamIdForVisibility,
    hasTemplateSharingManagePermission,
    isTemplateFromUserTeam,
  };
};
